import type { CallType } from '@klaro7/shared-types';

import { ensureMediaPermissions } from './mediaPermissions';
import { unlockRemoteAudio, type UnlockResult } from './remoteAudio';

export type CallPreflightResult =
  | { ok: true }
  | { ok: false; reason: 'permission'; message: string }
  | { ok: false; reason: 'audio-blocked' };

/**
 * Pre-flight run inside the Chiama/Rispondi/Rientra tap, before any signaling.
 *
 * The audio unlock is STARTED FIRST, synchronously in the gesture, and only
 * then do we wait on the device permission. The other order (await the mic
 * prompt, then play()) lets iOS WebKit expire the tap's user activation while
 * the prompt is up, so play() rejects with NotAllowedError and the answer is
 * refused — on the first tap only, since the retry finds the permission
 * already granted. Seen on 7–17% of iOS call attempts depending on the browser.
 */
export async function runCallPreflight(callType: CallType): Promise<CallPreflightResult> {
  if (callType === 'chat') return { ok: true };

  const unlockPromise: Promise<UnlockResult> = unlockRemoteAudio();
  const permissionError = await ensureMediaPermissions(callType);
  const unlock = await unlockPromise;

  if (permissionError) return { ok: false, reason: 'permission', message: permissionError };
  if (unlock === 'blocked') return { ok: false, reason: 'audio-blocked' };
  return { ok: true };
}
